"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
const wp_term_query_params = {
  taxonomy: "product_cat",
  per_page: 5,
  orderby: "count",
  order: "desc"
};
const wp_term_query = new WP_Term_Query(wp_term_query_params);
function CategoryGrid() {
  const {
    terms,
    loading,
    error,
    refetch
  } = useEcommerceTerms();
  if (loading) {
    return <div data-section-id="113675" className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        {[...Array(5)].map((_, i) => <Card key={i} data-index={i} className="flex h-full flex-col animate-pulse border-border shadow-sm">
            <div className="h-40 w-full rounded-t-xl bg-muted/50" />
            <CardHeader>
              <div className="h-6 w-3/4 rounded bg-muted/50" />
            </CardHeader>
            <CardContent className="flex-grow">
              <div className="space-y-2">
                <div className="h-4 w-full rounded bg-muted/50" />
                <div className="h-4 w-5/6 rounded bg-muted/50" />
                <div className="h-4 w-4/6 rounded bg-muted/50" />
              </div>
            </CardContent>
            <CardFooter>
              <div className="h-9 w-full rounded bg-muted/50" />
            </CardFooter>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border p-12 text-center">
        <p className="mb-4 text-muted-foreground">Failed to load product categories.</p>
        <Button variant="outline" onClick={refetch}>Retry Connection</Button>
      </div>;
  }
  if (terms.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border p-12 text-center">
        <p className="text-muted-foreground">No product categories available at this time.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
      {terms.map((term, i) => <Card key={term.id} data-index={i} className="group flex h-full flex-col overflow-hidden border-border bg-card shadow-sm transition-all duration-500 hover:border-primary/40 hover:shadow-xl hover:-translate-y-1">
          {term.image_url && <div className="aspect-video w-full overflow-hidden bg-accent/30">
              <img src={term.image_url} alt={term.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
            </div>}
          <CardHeader className="pb-4">
            <CardTitle className="font-sans text-lg font-bold tracking-tight text-secondary group-hover:text-primary transition-colors">
              {term.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow pb-6">
            {term.description ? <p className="text-sm leading-relaxed text-muted-foreground line-clamp-4">
                {term.description}
              </p> : <p className="text-sm leading-relaxed text-muted-foreground">
                Discover our signature okfun {term.name.toLowerCase()} collection, where clinical precision meets everyday wellness for your home.
              </p>}
          </CardContent>
          <CardFooter className="pt-0 mt-auto">
            <Button asChild variant="ghost" className="w-full justify-between border border-border/50 bg-accent/40 text-primary hover:bg-primary hover:text-white transition-all duration-300">
              <Link to={term.link || "#"}>
                Explore
                <span aria-hidden="true" className="ml-2">→</span>
              </Link>
            </Button>
          </CardFooter>
        </Card>)}
    </div>;
}
export default function ProductCategories() {
  return <section id="the-four-pillars-of-okfun" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 flex flex-col items-center text-center md:mb-16">
          <h2 className="mb-4 font-sans text-3xl font-bold tracking-tight text-secondary md:text-4xl lg:text-5xl">
            The Four Pillars of okfun
          </h2>
          <p className="max-w-3xl text-lg leading-relaxed text-muted-foreground">
            Experience a new standard in healthcare and lifestyle. Our curated collections are designed to provide professional-grade reliability with the comfort and ease of modern living.
          </p>
          <Separator className="mt-8 w-24 bg-primary/30" />
        </div>

        <EcommerceTermsProvider className="product-categories-grid-wrapper" wp_term_query={wp_term_query}>
          <CategoryGrid />
        </EcommerceTermsProvider>
      </div>
    </section>;
}